// Grids & Gantts — the two controls a structure row carries.
//
// StatusPick — change a status WITHOUT opening the work item. The list is
// not "every status on the site": it is the transitions this item's
// workflow actually offers from where it stands, asked for at the moment
// the reader clicks. Anything else would offer moves the workflow will
// refuse, and a control that offers refusals is worse than no control.
// A transition with a screen behind it is not silently guessed at — it is
// named as such and handed to the full work-item screen, because filling a
// mandatory field from a dropdown in a grid is not a thing that can be done
// honestly.
//
// CreatePlus — the "+" that creates work INTO the structure along the
// architectural hierarchy (see hierarchy.js). It offers only the work types
// the site's creation tree allows under this row, and wires the new item up
// on the edge the tree named.
//
// Both are lazy: nothing is fetched until the control is opened, because a
// structure draws hundreds of rows and none of them should cost a request
// until somebody means it.

import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import CreateIssueModal from '@modules/database/work-items/frontend/components/CreateIssueModal';
import { FONT } from './theme';
import Sheet from '../shared/Sheet';
import { CREATE_SCREEN_CSS } from '../shared/createScreenCss';
import { useIsPaneActive } from '@platform/app-shell/frontend/contexts/TabContext';
import { childOptionsFor, nothingToCreate, projectIdOf, projectOf, typeNameOf, warmHierarchy } from './hierarchy';
import { categoryWord, statusFill } from './icons';

// A small menu that closes on an outside click or Escape.
//
// A MENU MUST BE PORTALLED, and the reason is worth writing down because
// the symptom lies. These menus open from inside a grid row, and a row's
// frozen column pane is `position: sticky; z-index: 1` — which creates a
// STACKING CONTEXT. Inside one, a child's z-index is only compared with its
// siblings, so `z-index: 3000` on the menu competes with nothing useful and
// every row painted after it goes on top. The menu is still there and still
// has its own background; it is simply underneath the rest of the grid, so
// it reads as washed-out text that cannot be clicked.
//
// No z-index fixes that. The element has to leave the stacking context
// altogether, which is what a portal to <body> does.
// Its own popover is gone: it is the shared one, which is an anchored menu
// at a desk and a SHEET on a phone — see ../shared/Sheet. A status list
// hanging off a chip near the foot of a 375px screen opened off the bottom
// of it, under the thumb that was reaching for it.
const Menu = ({ anchorRef, onClose, ui, children, width = 240, narrow = false, title = '' }) => (
  <Sheet anchorRef={anchorRef} onClose={onClose} ui={ui} width={width} narrow={narrow} title={title} maxHeight={280}>
    {children}
  </Sheet>
);

const itemStyle = (ui) => ({
  display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none',
  padding: '6px 9px', borderRadius: 6, cursor: 'pointer', fontSize: 12.5, color: ui.fg, fontFamily: FONT,
});

// ---- status ------------------------------------------------------------------
export function StatusPick({ item, text, tone, ui, S, api, onChanged, onOpenItem, disabled, narrow = false }) {
  const anchor = useRef(null);
  const [open, setOpen] = useState(false);
  const [list, setList] = useState(null);     // null = loading
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const fill = statusFill(item && item.fields && item.fields.status);
  const chipInner = (
    <span title={`${text} — ${categoryWord(item && item.fields && item.fields.status)}`} style={{
      display: 'inline-block', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis',
      padding: '3px 8px', borderRadius: 4, fontSize: 10.5, fontWeight: 800, letterSpacing: '0.03em',
      textTransform: 'uppercase', background: busy ? ui.amber : fill, color: '#fff',
      border: '1px solid transparent',
    }}>{busy ? '…' : text}</span>
  );
  if (disabled || !item) return <span title={text} style={{ maxWidth: '100%', overflow: 'hidden' }}>{chipInner}</span>;

  // THE WHOLE CELL IS THE TARGET. A status chip is eighty pixels of text in
  // a cell twice as wide; making the reader hit the text exactly is why this
  // read as not working. The caret says it opens, so nobody has to discover
  // that by accident.
  const chip = (
    <span title={`${text} — click to move it on`} style={{
      display: 'inline-flex', alignItems: 'center', gap: 4, width: '100%', height: '100%',
      cursor: 'pointer', overflow: 'hidden',
      // THE CELL'S OWN PADDING, carried by the target rather than left on the
      // cell around it. The cell gives it up so that the twelve pixels down
      // each side are clickable too; whatever fills the cell has to put them
      // back, or the chip sits flush against the column's border.
      padding: '0 12px', boxSizing: 'border-box',
    }}>
      {chipInner}
      <span style={{ flex: '0 0 auto', color: ui.faint, fontSize: 9 }}>▾</span>
    </span>
  );

  const load = async () => {
    setList(null); setErr(null);
    try {
      const r = await api.getTransitions(item.key);
      const t = (r && (r.transitions || r.data?.transitions)) || (Array.isArray(r) ? r : []);
      setList(t);
    } catch (e) {
      setErr((e && e.response && e.response.data && e.response.data.error) || (e && e.message) || 'could not be read');
      setList([]);
    }
  };

  const go = async (t) => {
    setOpen(false);
    // A transition with a screen wants fields this cell cannot honestly
    // collect — hand the reader the whole screen instead of guessing.
    if (t.hasScreen || t.screen || (t.fields && Object.keys(t.fields).length)) { onOpenItem(item); return; }
    setBusy(true); setErr(null);
    try {
      await api.transitionIssue(item.key, t.id);
      const fresh = await api.getIssue(item.key, 'status,summary,duedate').catch(() => null);
      onChanged(item.key, fresh && fresh.fields ? { status: fresh.fields.status } : { status: { name: t.to?.name || t.name, statusCategory: t.to?.statusCategory } });
    } catch (e) {
      setErr((e && e.response && e.response.data && (e.response.data.details || e.response.data.error)) || (e && e.message) || 'refused');
    } finally { setBusy(false); }
  };

  return (
    <span ref={anchor} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, width: '100%', height: '100%', minWidth: 0 }}>
      <span onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); if (!open) load(); }}
        style={{ flex: '1 1 auto', minWidth: 0, height: '100%', display: 'inline-flex', alignItems: 'center', overflow: 'hidden' }}>{chip}</span>
      {err ? <span title={err} style={{ color: ui.red, fontSize: 11, cursor: 'help' }}>⚠</span> : null}
      {open && (
        <Menu anchorRef={anchor} onClose={() => setOpen(false)} ui={ui} width={250} narrow={narrow} title={item ? `${item.key} — move it on` : 'Status'}>
          <div style={{ ...S.tiny, padding: '4px 9px', fontWeight: 700 }}>Move {item.key} to…</div>
          {list === null ? <div style={{ ...S.tiny, padding: '6px 9px' }}>reading the workflow…</div> : null}
          {list && list.length === 0 ? <div style={{ ...S.tiny, padding: '6px 9px' }}>{err ? `The workflow ${err}` : 'The workflow offers no move from here.'}</div> : null}
          {/* Every target wears the colour of the category it lands in, so
              the reader can see they are about to move something to Done
              without having to know which of forty status names that is. */}
          {(list || []).map((t) => {
            const to = (t.to || {});
            const f = statusFill(to);
            return (
              <button key={t.id} onClick={() => go(t)}
                style={{ ...itemStyle(ui), display: 'flex', alignItems: 'center', gap: 7 }}
                onMouseEnter={(e) => { e.currentTarget.style.background = ui.surface; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}>
                <span style={{
                  flex: '0 0 auto', padding: '3px 8px', borderRadius: 4, background: f, color: '#fff',
                  fontSize: 10, fontWeight: 800, letterSpacing: '0.03em', textTransform: 'uppercase',
                  maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>{to.name || t.name}</span>
                <span style={{ ...S.tiny, flex: '1 1 auto', textAlign: 'right' }}>
                  {categoryWord(to)}{(t.hasScreen || t.screen) ? ' · needs the screen' : ''}
                </span>
              </button>
            );
          })}
        </Menu>
      )}
    </span>
  );
}

// ---- create ------------------------------------------------------------------
// THE "+" PICKS A TYPE, AND THAT IS ALL IT DOES. The summary is typed in the
// tree, on the row the new work item is about to become, under its parent —
// see draftRow in Grid.jsx and startDraft in Wip.jsx. A create with nothing
// to it but a summary is instant; the box this used to open in the menu was
// a dialog for a thing that is not a dialog.
//
// THE FULL CREATE SCREEN OPENS ONLY WHEN THE WORKFLOW DEMANDS FIELDS. The
// page dry-runs the Create validators the moment a type is picked; if they
// want anything beyond a summary, the product's own create screen opens
// instead of the row (CreateScreen below), with every demanded field ringed
// in red. It is also the fallback when a create from the row is refused for
// a field. The new row lands under its parent; its screen does not open.
export function CreatePlus({ item, ui, S, api, onCreated, onDraft = null, narrow = false }) {
  const anchor = useRef(null);
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState(null);   // null = loading
  const [err, setErr] = useState(null);
  // Without a page to draw the row in, the screen is the fallback.
  const [screen, setScreen] = useState(null);
  // A WORK TYPE THE HIERARCHY ALLOWS NOTHING UNDER GETS NO "+" — a button
  // whose only answer is "nothing here" is not a button. Asked of the
  // per-project caches the grid fills when it opens; the space is kept, so
  // the names stay in line.
  const [none, setNone] = useState(false);
  const typeName = typeNameOf(item);
  useEffect(() => {
    let on = true;
    nothingToCreate(api, item).then((n) => { if (on) setNone(n); }).catch(() => {});
    return () => { on = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, item && item.key, typeName]);

  const load = async () => {
    setOptions(null); setErr(null);
    try { setOptions(await childOptionsFor(api, item)); }
    catch (e) { setErr((e && e.message) || 'the hierarchy could not be read'); setOptions([]); }
  };

  const pick = (o) => {
    setOpen(false);
    if (onDraft) onDraft(item, o, options || [o]);
    else setScreen({ option: o, options: options || [o] });
  };

  if (none && !open && !screen) return <span style={{ flex: '0 0 17px', width: 17 }} />;

  return (
    <span ref={anchor} style={{ display: 'inline-flex', flex: '0 0 auto' }}>
      <button onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); if (!open) load(); }}
        // Warming on hover is most of the speed: by the time a click lands,
        // the scheme, the tree and the project's work types are usually
        // already in hand, and the menu opens on the spot.
        onMouseEnter={() => warmHierarchy(api, item)}
        onFocus={() => warmHierarchy(api, item)}
        title={`Create work under ${item.key}, along the hierarchy`}
        className="wip-plus"
        style={{
          background: 'none', border: `1px solid ${open ? ui.blue : ui.border}`, borderRadius: 4,
          padding: 0, width: 17, height: 17, flex: '0 0 17px', cursor: 'pointer',
          color: open ? ui.blue : ui.muted, fontSize: 13, lineHeight: 1, fontFamily: FONT, fontWeight: 700,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        }}>+</button>
      {open && (
        <Menu anchorRef={anchor} onClose={() => setOpen(false)} ui={ui} width={280} narrow={narrow} title={`Will be created under ${item.key}`}>
          <div style={{ ...S.tiny, padding: '4px 9px', fontWeight: 700 }}>Will be created under {item.key}</div>
          {options === null ? <div style={{ ...S.tiny, padding: '6px 9px' }}>reading the hierarchy…</div> : null}
          {options && options.length === 0 ? (
            <div style={{ ...S.tiny, padding: '6px 9px' }}>
              {err || 'The hierarchy allows nothing under this work type.'}
            </div>
          ) : null}
          {(options || []).map((o) => (
            <button key={o.issueTypeId} onClick={() => pick(o)} style={itemStyle(ui)}
              onMouseEnter={(e) => { e.currentTarget.style.background = ui.surface; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}>
              {o.iconUrl ? <img src={o.iconUrl} alt="" style={{ width: 13, height: 13, marginRight: 6, verticalAlign: -2 }} /> : null}
              {o.issueTypeName}
              <span style={{ ...S.tiny, marginLeft: 6 }}>
                {o.edge && (o.edge.relationshipType === 'linked' ? `· ${o.edge.linkTypeName}` : '· child')}
              </span>
            </button>
          ))}
        </Menu>
      )}
      {screen ? (
        <CreateScreen item={item} option={screen.option} options={screen.options} api={api}
          onClose={() => setScreen(null)}
          // The screen closes itself after a create — unless Create another
          // is ticked, when it stays open for the next one.
          onCreated={(parent, made) => onCreated(parent, made)} />
      ) : null}
    </span>
  );
}

// The product's full create screen, in hierarchy mode, for THIS row and
// the picked type. It dry-runs the validators and marks what they demand;
// it makes the item and joins it the way the edge says; it hands back the
// result. What is added around it is one stylesheet while it is open — the
// layers, the look and the footer: see ../shared/createScreenCss.js.
// `summary` is what the reader already typed on the row: the screen opens
// with it filled in, rather than asking for it a second time.
export function CreateScreen({ item, option, options, api, onCreated, onClose, summary = '' }) {
  const [projectId, setProjectId] = useState(undefined);
  // Portalled to <body>, so it would cover another tab: drawn only while
  // its own tab is in front. (Its overlay covers the tab bar, so only a
  // page opening a tab on its own can get here; the half-typed form goes.)
  const inFront = useIsPaneActive();
  // THE HIERARCHY PROP MUST BE STABLE. CreateIssueModal reloads (and clears
  // its fields) whenever the object changes, then re-picks the SAME type —
  // so the screen effect never runs again and the form stays empty. An
  // inline object here did that on every re-render of the page around the
  // "+" (a warehouse list refreshing under the open screen).
  const hierarchy = useMemo(() => ({ options: options || [option], initialTypeId: option.issueTypeId }), [options, option]);
  // The project id by the row's KEY, not the row object: a page that
  // rebuilds its rows hands a new object on every render.
  const itemKey = item && item.key;
  useEffect(() => {
    let on = true;
    projectIdOf(api, item).then((id) => { if (on) setProjectId(id || null); });
    return () => { on = false; };
  }, [api, itemKey]); // eslint-disable-line react-hooks/exhaustive-deps
  if (projectId === undefined || typeof document === 'undefined' || !inFront) return null;
  return createPortal(
    <>
      <style>{CREATE_SCREEN_CSS}</style>
      <CreateIssueModal
        isOpen
        mode="hierarchy"
        projectKey={projectOf(item)}
        projectId={projectId}
        parentIssueKey={item.key}
        hierarchy={hierarchy}
        initialSummary={summary}
        onClose={onClose}
        onCreated={(result, mode, edge) => {
          const key = result && (result.key || (result.issue && result.issue.key));
          if (key) onCreated(item, { key, isChild: !!(edge && (edge.relationshipType === 'subtask' || edge.relationshipType === 'child')), edge: edge || null });
        }} />
    </>,
    document.body,
  );
}
